import { type OidcProviderPublicSummary, oidcApi } from '@/api/oidc';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function oidcStartUrl(slug: string, returnTo?: string): string {
  const base = `/api/v1/auth/oidc/${slug}/start`;
  // The backend only accepts a same-origin absolute path here and silently
  // falls back to /projects otherwise, so callers must pass a pathname.
  return returnTo ? `${base}?returnTo=${encodeURIComponent(returnTo)}` : base;
}

/**
 * The provider list, shared by the login page and the re-authentication modal.
 * The login page hides the email alternative behind a secondary control and
 * makes these the primary action; the modal keeps its inline divider.
 */
export function SsoButtons({
  returnTo,
  showDivider = true,
  emphasis = 'outline',
  onLoaded,
}: {
  returnTo?: string;
  showDivider?: boolean;
  emphasis?: 'default' | 'outline';
  onLoaded?: (providerCount: number) => void;
}) {
  const { t } = useTranslation();
  const [providers, setProviders] = useState<OidcProviderPublicSummary[]>([]);

  useEffect(() => {
    let cancelled = false;
    oidcApi
      .listPublic()
      .then((list) => {
        if (!cancelled) {
          setProviders(list);
          onLoaded?.(list.length);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProviders([]);
          onLoaded?.(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [onLoaded]);

  if (providers.length === 0) return null;

  return (
    <>
      <div className="flex flex-col gap-2.5">
        {providers.map((p) => (
          <Button
            key={p.slug}
            variant={emphasis}
            className="h-11 w-full gap-2"
            onClick={() => window.location.assign(oidcStartUrl(p.slug, returnTo))}
          >
            <LogIn
              data-icon="inline-start"
              className={
                emphasis === 'default' ? 'text-primary-foreground/75' : 'text-muted-foreground'
              }
            />
            {t('auth.sso_with', {
              name: p.display_name,
              defaultValue: `Continue with ${p.display_name}`,
            })}
          </Button>
        ))}
      </div>
      {showDivider && (
        <div className="mt-4 flex items-center gap-3">
          <Separator className="flex-1" />
          <span className="text-xs whitespace-nowrap text-subtle-foreground">
            {t('auth.or_email', 'or with email')}
          </span>
          <Separator className="flex-1" />
        </div>
      )}
    </>
  );
}
